import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconCopy, IconPencil, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
];

type FlowChannel = 'whatsapp' | 'messenger' | 'instagram';

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: 'green',
  messenger: 'blue',
  instagram: 'grape',
};

function ChannelBadges({ channels }: { channels: string }) {
  const list = channels
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  return (
    <Group gap={4}>
      {list.map((c) => (
        <Badge key={c} size="sm" variant="light" color={CHANNEL_COLORS[c] ?? 'gray'}>
          {c}
        </Badge>
      ))}
    </Group>
  );
}

function FlowsPage() {
  const list = trpc.bots.flows.list.useQuery();
  const utils = trpc.useUtils();
  const create = trpc.bots.flows.create.useMutation({
    onSuccess: () => {
      void utils.bots.flows.list.invalidate();
      notifications.show({ message: 'Flow created', color: 'green' });
    },
  });
  const update = trpc.bots.flows.update.useMutation({
    onSuccess: () => void utils.bots.flows.list.invalidate(),
  });
  const dup = trpc.bots.flows.duplicate.useMutation({
    onSuccess: () => {
      void utils.bots.flows.list.invalidate();
      notifications.show({ message: 'Flow duplicated', color: 'green' });
    },
  });
  const del = trpc.bots.flows.delete.useMutation({
    onSuccess: () => void utils.bots.flows.list.invalidate(),
  });

  const [opened, { open, close }] = useDisclosure(false);
  const [title, setTitle] = useState('');
  const [startTrigger, setStartTrigger] = useState('');
  const [channels, setChannels] = useState<string[]>(['whatsapp']);

  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editTrigger, setEditTrigger] = useState('');
  const [editChannels, setEditChannels] = useState<string[]>(['whatsapp']);
  const [editStatus, setEditStatus] = useState(2);
  const startEdit = (f: {
    id: number;
    title: string;
    startTrigger: string | null;
    channels: string;
    status: number;
  }) => {
    setEditId(f.id);
    setEditTitle(f.title);
    setEditTrigger(f.startTrigger ?? '');
    setEditChannels(f.channels.split(',').filter(Boolean));
    setEditStatus(f.status);
    openEdit();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Chat flows</Title>
          <Button leftSection={<IconCirclePlus size={14} />} onClick={open}>
            New flow
          </Button>
        </Group>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Start trigger</Table.Th>
                  <Table.Th>Channels</Table.Th>
                  <Table.Th>Runs</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((f) => (
                  <Table.Tr key={f.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link
                            to="/vendor/bots/flows/$flowId/builder"
                            params={{ flowId: String(f.id) }}
                            {...props}
                          />
                        )}
                      >
                        {f.title}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {f.startTrigger ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ChannelBadges channels={f.channels} />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Times this flow was triggered">
                        <Badge variant="light">{f.runs ?? 0}</Badge>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={f.status === 1}
                        onChange={(e) =>
                          update.mutate({
                            id: f.id,
                            title: f.title,
                            startTrigger: f.startTrigger,
                            channels: f.channels.split(',').filter(Boolean) as FlowChannel[],
                            status: e.currentTarget.checked ? 1 : 2,
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="end">
                        <ActionIcon variant="subtle" aria-label="Edit" onClick={() => startEdit(f)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          aria-label="Duplicate"
                          onClick={() => dup.mutate({ id: f.id })}
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          aria-label="Delete"
                          onClick={() => del.mutate({ id: f.id })}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        No flows yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Modal opened={opened} onClose={close} title="New flow" centered>
          <Stack gap="sm">
            <TextInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <TextInput
              label="Start trigger keyword"
              description="The keyword the contact must send to enter this flow"
              value={startTrigger}
              onChange={(e) => setStartTrigger(e.currentTarget.value)}
            />
            <MultiSelect
              label="Channels"
              description="Where this flow's start trigger fires (the flow graph is the same)"
              data={CHANNEL_OPTIONS}
              value={channels}
              onChange={setChannels}
              clearable={false}
            />
            <Button
              loading={create.isPending}
              onClick={() => {
                if (!title.trim()) return;
                create.mutate({
                  title,
                  startTrigger: startTrigger || null,
                  channels: (channels.length ? channels : ['whatsapp']) as FlowChannel[],
                  status: 2,
                });
                close();
                setTitle('');
                setStartTrigger('');
                setChannels(['whatsapp']);
              }}
            >
              Create
            </Button>
          </Stack>
        </Modal>

        <Modal opened={editOpened} onClose={closeEdit} title="Edit flow" centered>
          <Stack gap="sm">
            <TextInput
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.currentTarget.value)}
            />
            <TextInput
              label="Start trigger keyword"
              description="The keyword the contact must send to enter this flow"
              value={editTrigger}
              onChange={(e) => setEditTrigger(e.currentTarget.value)}
            />
            <MultiSelect
              label="Channels"
              description="Where this flow's start trigger fires"
              data={CHANNEL_OPTIONS}
              value={editChannels}
              onChange={setEditChannels}
              clearable={false}
            />
            <Switch
              label="Active"
              checked={editStatus === 1}
              onChange={(e) => setEditStatus(e.currentTarget.checked ? 1 : 2)}
            />
            <Button
              loading={update.isPending}
              onClick={() => {
                if (editId == null || !editTitle.trim()) return;
                update.mutate({
                  id: editId,
                  title: editTitle,
                  startTrigger: editTrigger || null,
                  channels: (editChannels.length ? editChannels : ['whatsapp']) as FlowChannel[],
                  status: editStatus,
                });
                closeEdit();
              }}
            >
              Save
            </Button>
          </Stack>
        </Modal>
      </Stack>
    </motion.div>
  );
}

function FlowsRoute() {
  return (
    <PermissionGate permission="manage_bots">
      <FlowsPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/bots/flows/')({ component: FlowsRoute });
